import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Stack,
} from "@mui/material";
import dayjs from "dayjs";
import ContentModal from "components/ContentModal";
import Api from "api";
import { ACTIONS } from "./Constants";

const getDefaultYearOptions = () => {
  const currentYear = dayjs().year();
  return [
    (currentYear - 1).toString(),
    currentYear.toString(),
    (currentYear + 1).toString(),
    (currentYear + 2).toString(),
  ];
};

/**
 * Returns whether a workflow is explicitly enabled for bulk policy updates.
 */
const isBulkActionWorkflowEnabled = (workflow) => {
  const enabled =
    workflow?.bulkActionEnabled ?? workflow?.BulkActionEnabled ?? false;

  if (typeof enabled === "boolean") {
    return enabled;
  }

  if (typeof enabled === "number") {
    return enabled === 1;
  }

  if (typeof enabled === "string") {
    return ["true", "yes", "y", "1"].includes(enabled.trim().toLowerCase());
  }

  return false;
};

const CreatePolicyUpdateDialog = ({ open, handleClose, handleConfirm }) => {
  const [cabQuarter, setCabQuarter] = useState("");
  const [cabYear, setCabYear] = useState("");
  const [categoryOfWork, setCategoryOfWork] = useState("");
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);

  // fetch (allowed: setState occurs in async callback)
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await Api.policyEnforcementGrid.getBulkActionWorkflow();
        if (!cancelled) {
          setWorkflows((resp.data || []).filter(isBulkActionWorkflowEnabled));
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) setLoading(false);
        alert("Error fetching workflows");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // derive the selected category (no effects)
  const selectedCategory = useMemo(
    () =>
      workflows.find(
        (w) => w.bulkActionWorkflowReferenceKey === categoryOfWork
      ),
    [workflows, categoryOfWork]
  );

  // derive years/quarters (no effects, no local setState)
  const { yearOptions, quarterOptions } = useMemo(() => {
    if (!selectedCategory) {
      return { yearOptions: [], quarterOptions: [] };
    }
    const years = selectedCategory?.yearText
      ? selectedCategory.yearText.split(",")
      : getDefaultYearOptions();

    const display = selectedCategory.identifierText
      ? selectedCategory.identifierText.split(",")
      : (selectedCategory.quarterAlias || "").split(",");

    const value = selectedCategory.quarterAlias
      ? selectedCategory.quarterAlias.split(",")
      : (selectedCategory.identifierText || "").split(",");

    const quarters = display.map((d, idx) => ({
      value: value[idx],
      display: d,
    }));
    return { yearOptions: years, quarterOptions: quarters };
  }, [selectedCategory]);

  // move resets here (event handler), not in an effect
  const handleCategoryChange = (e) => {
    setCategoryOfWork(e.target.value);
    setCabQuarter("");
    setCabYear("");
  };

  const handlePolicyUpdateConfirm = () => {
    handleConfirm({
      action: ACTIONS.UPDATE_POLICY,
      cabQuarter,
      cabYear,
      categoryOfWork,
    });
  };

  const isConfirmDisabled = !cabQuarter || !cabYear || !categoryOfWork;

  return (
    <ContentModal
      open={open}
      handleClose={handleClose}
      title={
        <Box
          sx={{
            display: "flex",
            fontWeight: "bold",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <span>Policy Update</span>
        </Box>
      }
      content={
        <Box>
          <Typography marginBottom={2}>
            Select the Category of Work, CAB Quarter and year for updating
            policy (or policies) across all health plans.
          </Typography>

          <FormControl
            fullWidth
            margin="normal"
            size="small"
            disabled={loading}
          >
            <InputLabel id="category-of-work-label">
              Category of Work
            </InputLabel>
            <Select
              labelId="category-of-work-label"
              id="category-of-work-select"
              value={categoryOfWork}
              onChange={handleCategoryChange}
              label="Category of Work"
            >
              {!loading &&
                workflows.map((wf) => (
                  <MenuItem
                    key={wf.bulkActionWorkflowReferenceKey}
                    value={wf.bulkActionWorkflowReferenceKey}
                  >
                    {wf.categoryOfWorkName}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={2}>
            <FormControl fullWidth margin="normal" size="small">
              <InputLabel id="cab-quarter-label">CAB Quarter</InputLabel>
              <Select
                labelId="cab-quarter-label"
                id="cab-quarter-select"
                value={cabQuarter}
                onChange={(e) => setCabQuarter(e.target.value)}
                label="CAB Quarter"
                disabled={!quarterOptions.length}
              >
                {quarterOptions.map((q) => (
                  <MenuItem key={q.value} value={q.value}>
                    {q.display}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal" size="small">
              <InputLabel id="cab-year-label">CAB Year</InputLabel>
              <Select
                labelId="cab-year-label"
                id="cab-year-select"
                value={cabYear}
                onChange={(e) => setCabYear(e.target.value)}
                label="CAB Year"
                disabled={!yearOptions.length}
              >
                {yearOptions.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      }
      actions={
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}
        >
          <Button
            onClick={handlePolicyUpdateConfirm}
            color="primary"
            disabled={isConfirmDisabled}
          >
            OK
          </Button>
        </Box>
      }
    />
  );
};

export default CreatePolicyUpdateDialog;
